import { PdsWidget } from '../../pds-widget';
import { SortConfiguration } from '../../models/grid-widget/sort-configuration';
import { GridWidget } from '../../models/grid-widget/grid-widget';
import { PropertyObserver } from '../../models/property-observer';
import { pdsLogger } from '../../pds-logger';
import { pdsUtils } from '../../pds-utils';
import { serviceInstances } from '../../services/service-instances';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('gridWidget'), 'pds-grid-widget-sort-fields.ts');

export class PdsGridWidgetSortFields extends PdsWidget {
  is = 'pds-grid-widget-sort-fields';

  //Input -> This is required to be set as a property.
  sortConfiguration: SortConfiguration | null = null;

  //Input -> Used to fetch the grid widget object.
  dataGridWidgetId: string | null = null;

  //Not an Input - This is looked up via the GridWidget Service
  gridWidget: GridWidget | null;

  isAttached = false;

  properties = {
    sortConfiguration: {
      observer: 'updateSortConfiguration',
      value: null
    },
    dataGridWidgetId: {
      type: String,
      observer: 'gridWidgetChange',
      value: null
    }
  };

  listeners = {
    click: 'toggleSortDirection',
    keydown: 'toggleSortDirectionViaKeyBoard'
  };

  private reverseObserver: PropertyObserver | undefined;

  ready(): void {
    super.ready();

    //Transfer all nodes that aren't .pds-grid-widget-sort-fields-content
    let target: HTMLElement | undefined = this.querySelector('.pds-grid-widget-sort-fields-content-target');
    if (target != null) {
      for (let i = 0; i < this.childNodes.length; i++) {
        let node: Node = this.childNodes[i];
        if (pdsUtils.is(<HTMLElement>node, '.pds-grid-widget-sort-fields-content')) {
          continue;
        }

        target.appendChild(node); //actually moves the node.
      }
    }
  }

  attached(): void {
    super.attached();
    this.isAttached = true;
    //Makes sure that the watch gets added for sortConfiguration.reversed
    this.updateSortConfiguration(this.sortConfiguration, undefined);
  }

  getSortClass(): string {
    if (this.sortConfiguration == null || this.sortConfiguration.reversed == null) {
      return '';
    }

    return this.sortConfiguration.reversed ? 'pds-sort-descending' : 'pds-sort-ascending';
  }

  updateSortConfiguration(newSortConfiguration: SortConfiguration | null | undefined, oldSortConfiguration: SortConfiguration | null | undefined): void {
    // if there was an old sort config, disable the reverse property observer
    if (oldSortConfiguration != null && this.reverseObserver) {
      this.reverseObserver.disable();
      this.reverseObserver = undefined;
    }

    // add the reverse property observer for the new sort config
    if (newSortConfiguration != null && this.isAttached) {
      //we don't want to add the watch until after the component has been attached.
      this.reverseObserver = this.watch('sortConfiguration.reversed');
    }
  }

  toggleSortDirectionViaKeyBoard(event: KeyboardEvent): void {
    // space and enter
    if (event.keyCode === 32 || event.keyCode === 13) {
      this.toggleSortDirection();
    }
  }

  toggleSortDirection(): void {
    this.validateWidgetConfiguration();

    if (this.gridWidget != null && this.sortConfiguration != null) {
      //Flip the direction here. Everything else gets done further down the chain.
      //States:
      // reversed = false       =>   Ascending sort
      // reversed = true        =>   Descending sort
      // reversed = null   =>   No sort
      if (this.sortConfiguration.reversed == null) {
        this.sortConfiguration.reversed = false;
        this.gridWidget.setSortConfig(this.sortConfiguration);
      } else if (this.sortConfiguration.reversed === false) {
        this.sortConfiguration.reversed = true;
        this.gridWidget.setSortConfig(this.sortConfiguration);
      } else if (this.sortConfiguration.reversed === true) {
        this.sortConfiguration.reversed = null; //TODO: Gotta update documentation to reflect null rather than undefined.
        this.gridWidget.setSortConfig(undefined);
      }
      this.gridWidget.executeTaskChain('sort');
    }
  }

  gridWidgetChange(): void {
    if (this.dataGridWidgetId != null) {
      serviceInstances.getGridWidgetService().getGridWidget(this.dataGridWidgetId).subscribe((gridWidget: GridWidget | undefined) => {
        this.gridWidget = gridWidget || null;
      });
    }
  }

  validateWidgetConfiguration(): void {
    if (this.sortConfiguration == null) {
      //The pds-sort-field component used to blow up violently if no sort configuration object was passed in.
      //Just having this check on ngOnInit, and using the ? operator in the template for the sortConfiguation property
      //will make it a lot easier to debug for programmers.
      console.error('PdsSortFields requires a SortConfiguration object');
    }

    if (this.gridWidget == null) {
      console.error('PdsSortFields requires a Grid Widget ID');
    }
  }
}
